import { DragOverEvent } from '@dnd-kit/core';
import {
  CaseReducer,
  createSlice,
  Dictionary,
  PayloadAction,
} from '@reduxjs/toolkit';
import { get, set } from 'lodash';
import {
  DragStartEvent,
  DropEndResult,
} from 'components/organisms/kanban-board/types';
import {
  generateDroppableId,
  parseDraggableId,
  parseDroppableId,
} from 'components/organisms/kanban-board/utils';

type Item = string;
type Items = Item[];
export type Matrix = Dictionary<Dictionary<Items>>;

export type BoardState = Matrix | null;
type RowId = string;
type ColumnId = string;
type Cell = [RowId, ColumnId];

const Cell = (rowId: RowId, colId: ColumnId): Cell => [rowId, colId];

const sortItems = (source: unknown[] = [], target: unknown[] = []) => {
  return source.sort((a, b) => target.indexOf(a) - target.indexOf(b));
};

const initialState: BoardState = null;

const updateMatrix: CaseReducer<BoardState, PayloadAction<DropEndResult>> = (
  state: BoardState,
  { payload },
) => {
  const result = payload;

  if (!result.destination) {
    return state;
  }
  const sourceDroppableId = result.source.droppableId;
  const sourceDraggableId = result.source.draggableId;
  const destinationDroppableId = result.destination.droppableId;
  const destinationDraggableId = result.destination.draggableId;
  const [sourceRowId, sourceColumnId, sourceItemId] =
    parseDraggableId(sourceDraggableId);
  const [destinationRowId, destinationColumnId] = parseDroppableId(
    destinationDroppableId,
  );

  const [, , destinationItemId] =
    destinationDraggableId === 'END_OF_LIST'
      ? []
      : parseDraggableId(destinationDraggableId);

  // reordering in same list
  if (sourceDroppableId === destinationDroppableId) {
    return state;
  }

  if (!state) {
    return state;
  }

  if (
    !sourceRowId ||
    !sourceColumnId ||
    !destinationRowId ||
    !destinationColumnId ||
    !sourceItemId
  ) {
    return state;
  }

  // moving between lists
  const sourceColumn = get(state, [sourceRowId, sourceColumnId], []);

  const sourceIndex = [...sourceColumn].indexOf(sourceItemId);
  const destinationColumn = [
    ...get(state, [destinationRowId, destinationColumnId], []),
  ];

  const item = sourceColumn[sourceIndex];

  const destinationIndex =
    destinationDraggableId === 'END_OF_LIST'
      ? destinationColumn.length
      : destinationItemId
      ? destinationColumn.indexOf(destinationItemId)
      : 0;

  // 1. remove item from source column
  const newSourceColumn = [...sourceColumn];

  newSourceColumn.splice(sourceIndex, 1);

  // 2. insert into destination column
  const newDestinationColumn = [...destinationColumn];

  newDestinationColumn.splice(destinationIndex, 0, item);

  const sourceRow = state[sourceRowId];
  const destinationRow = state[destinationRowId];

  // dropping on the same swimlane
  if (sourceRow === destinationRow) {
    const updated: Matrix = {
      [sourceRowId]: {
        ...sourceRow,
        [sourceColumnId]: newSourceColumn,
        [destinationColumnId]: newDestinationColumn,
      },
    };

    return {
      ...state,
      ...updated,
    };
  }

  return {
    ...state,
    [sourceRowId]: {
      ...sourceRow,
      [sourceColumnId]: newSourceColumn,
    },
    [destinationRowId]: {
      ...destinationRow,
      [destinationColumnId]: newDestinationColumn,
    },
  };
};

const patchMatrix: CaseReducer<BoardState, PayloadAction<Matrix>> = (
  state: BoardState,
  { payload },
): BoardState => {
  if (state) {
    const cells: Cell[] = [];

    Object.keys(payload).forEach((rowId) => {
      const row = payload[rowId];

      if (row) {
        Object.keys(row).forEach((columnId) => {
          cells.push(Cell(rowId, columnId));
        });
      }
    });

    const result: Matrix = {};

    cells.forEach((cell) => {
      const newItems = [...get(payload, cell, [])];
      const existingItems = [...get(state, cell, [])];

      const sorted =
        existingItems.length > 0
          ? sortItems(newItems, existingItems)
          : newItems;

      set(result, cell, sorted);
    });

    return result;
  }

  return {
    ...payload,
  };
};

const createMatrix: CaseReducer<BoardState, PayloadAction<Matrix>> = (
  state: BoardState,
  { payload },
) => {
  return {
    ...payload,
  };
};

const dragStart: CaseReducer<BoardState, PayloadAction<DragStartEvent>> = (
  state,
  action,
) => {
  const { payload } = action;
  const { draggableId } = payload;
  const [sourceRowId, sourceColumnId, sourceItemId] =
    parseDraggableId(draggableId);

  if (!state) {
    return state;
  }
  const result = [...get(state, [sourceRowId, sourceColumnId], [])].filter(
    (itemId) => itemId !== sourceItemId,
  );

  return {
    ...state,
    [sourceRowId]: {
      ...state[sourceRowId],
      [sourceColumnId]: result,
    },
  };
};

const dragOver: CaseReducer<BoardState, PayloadAction<DragOverEvent>> = (
  state: BoardState,
  { payload },
) => {
  const { over, active } = payload;
  const overId = over?.id;
  const activeId = active.id;

  if (!overId || !over || !state) {
    return state;
  }

  const [activeRowId, activeColumnId, activeItemId] = parseDraggableId(
    active.id,
  );

  const [overRowId, overColumnId, overItemId] = parseDraggableId(overId);

  if (!overRowId || !overColumnId || !activeColumnId || !activeRowId) {
    return state;
  }
  const newState = Object.keys(state).reduce((obj, rowKey) => {
    const result = Object.keys(state[rowKey] ?? {}).reduce((colObj, colKey) => {
      return {
        ...colObj,
        [generateDroppableId(rowKey, colKey)]: get(state, [rowKey, colKey]),
      };
    }, {});

    return {
      ...obj,
      ...result,
    };
  }, {});

  const findContainer = (id: string) => {
    if (id in newState) {
      return id;
    }
    const [, , itemId] = parseDraggableId(id);

    return Object.keys(newState).find((key) =>
      [...get(newState, key, [])].includes(itemId),
    );
  };
  const activeContainerId = findContainer(activeId);
  const overContainerId = findContainer(overId);

  if (!activeContainerId || !overContainerId) {
    return state;
  }

  if (activeContainerId !== overContainerId) {
    const activeItems = [...(get(state, activeContainerId, []) as string[])];
    const overItems = [...(get(state, overContainerId, []) as string[])];
    const overIndex = overItems.indexOf(overItemId);

    let newIndex: number;

    if (overId === overContainerId) {
      newIndex = 0;
    } else {
      const isBelowLastItem =
        over &&
        overIndex === overItems.length - 1 &&
        active.rect.current.translated &&
        active.rect.current.translated.offsetTop >
          over.rect.offsetTop + over.rect.height;

      const modifier = isBelowLastItem ? 1 : 0;

      newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
    }
    // 1. remove item from source column
    const newActiveItems = activeItems.filter((id) => id !== activeItemId);
    // 2. insert into destination column
    const newOverItems = [...overItems].filter((id) => id !== activeItemId);

    newOverItems.splice(newIndex, 0, activeItemId);
    const [newActiveRowId, newActiveColumnId] =
      parseDroppableId(activeContainerId);
    const [newOverRowId, newOverColumnId] = parseDroppableId(overContainerId);

    if (
      !newActiveRowId ||
      !newActiveColumnId ||
      !newOverRowId ||
      !newOverColumnId
    ) {
      return state;
    }
    if (newActiveRowId === newOverRowId) {
      return {
        ...state,
        [newActiveRowId]: {
          ...state[newActiveRowId],
          [newActiveColumnId]: newActiveItems,
          [newOverColumnId]: newOverItems,
        },
      };
    }

    return {
      ...state,
      [newActiveRowId]: {
        ...state[newActiveRowId],
        [newActiveColumnId]: newActiveItems,
      },
      [newOverRowId]: {
        ...state[newOverRowId],
        [newOverColumnId]: newOverItems,
      },
    };
  }

  return state;
};

const reducers = {
  updateMatrix,
  patchMatrix,
  createMatrix,
  dragOver,
  dragStart,
};

type Reducers = typeof reducers;

export const boardStateSlice = createSlice<BoardState, Reducers>({
  initialState,
  name: 'task-board',
  reducers,
});
export const boardStateReducer = boardStateSlice.reducer;
